import { Code } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button, Compact, Dropdown, Input, Text, type MenuProps } from "~/components/ui";

interface TemplateEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  variables?: string[];
}

const DEFAULT_VARIABLES = ["value"];

export function TemplateEditor({
  value,
  onChange,
  placeholder = "Site: {value}",
  variables = DEFAULT_VARIABLES,
}: TemplateEditorProps) {
  const { t } = useTranslation("admin");
  const inputRef = useRef<HTMLInputElement>(null);

  const insertVariable = (variable: string) => {
    const input = inputRef.current;
    const insertion = `{${variable}}`;

    if (input) {
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? 0;
      const currentValue = value ?? "";
      const newValue = currentValue.slice(0, start) + insertion + currentValue.slice(end);
      onChange?.(newValue);

      setTimeout(() => {
        input.focus();
        const newPos = start + insertion.length;
        input.setSelectionRange(newPos, newPos);
      }, 0);
    } else {
      onChange?.((value ?? "") + insertion);
    }
  };

  const menuItems: MenuProps["items"] = variables.map((v) => ({
    key: v,
    label: <code className="text-xs bg-noa-inset px-1 rounded">{`{${v}}`}</code>,
    onClick: () => insertVariable(v),
  }));

  const hasPlaceholder = value?.includes("{") && value?.includes("}");

  const preview = value ? value.replace(/\{(\w+)\}/g, (_, name) => `[${name.toUpperCase()}]`) : null;

  return (
    <div className="space-y-1">
      <Compact className="w-full">
        <Input
          ref={inputRef}
          value={value ?? ""}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          className="font-mono"
        />
        <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
          <Button icon={<Code size={14} />}>{t("schemaAdmin.template.insertVar")}</Button>
        </Dropdown>
      </Compact>
      {value && (
        <Text tone="muted" className="text-xs block">
          {t("schemaAdmin.template.preview")}: <code className="bg-noa-inset px-1 rounded">{preview}</code>
          {!hasPlaceholder && (
            <Text tone="warning" className="ml-2">
              {t("schemaAdmin.template.noVariable")}
            </Text>
          )}
        </Text>
      )}
    </div>
  );
}
